<template>
  <div
    class="player"
    :class="playerClassName"
    :style="themeStyles"
    ref="container"
  >
    <!-- D-235 (A3): the same two channels the v4 sibling gains, for the same reason —
         a name CHANGE is not announced on a focused element, so D-234's focus fix is
         unobservable without this. -->
    <live-region :status="statusMessage" :failure="failureMessage"></live-region>
    <!-- 🔴 D-237 (A4): THIS INSTANCE PASSES `hide-close-button`, so backdrop-click and
         ESC are its ONLY close paths. The plan flagged a hazard here — ESC inside an
         `input[type="search"]` is ALSO the browser's clear-field gesture, so the first
         press might only clear the query and leave a keyboard user stuck in a modal with
         no close button.
         ✅ MEASURED 2026-09-08, chromium 1217, real browser, both cases: with "hello"
         typed, ONE press closes the modal; with the field empty, ONE press closes it.
         The clear-field default action does not stop propagation, so the window-level
         handler sees the key either way. The hazard does not reproduce — recorded as a
         measurement rather than removed, because the reasoning behind it is sound and
         the next person will think of it again.
         ⚠️ Chromium only. `webkit.launch()` does not run on this host (missing system
         libraries — the browser is installed), so Safari is unverified; CI's
         `e2e_vite_webkit` job is the only coverage. -->
    <modal
      v-if="isClassic() && searchIsVisible"
      centered
      hide-close-button
      label="Search episodes"
      background-color="rgba(0, 0, 0, 0.5)"
      @close="searchIsVisible = false"
    >
      <template #body>
        <search
          :search-term="searchTerm"
          :handle-on-press-search="handleOnPressSearch"
          :theme="configs.theme"
          ref="search"
        ></search>
      </template>
    </modal>
    <hash-tag-modal
      v-if="
        isClassic() &&
        configs.preferences.enableHashtags &&
        categories.length
      "
      :is-visible="hashTagModalIsVisible"
      :on-press-close="handleOnPressShowMore"
      :theme="configs.theme"
      :tags="categories"
    ></hash-tag-modal>
    <div class="player__columns">
      <div
        v-if="
          (configs.preferences.enableAlbumArt && podcast.image.length) ||
          isMini()
        "
        class="player__column"
        :class="{
          'player__column--shrink': isMini(),
          'player__column--grow': isClassic(),
        }"
      >
        <div
          v-if="configs.preferences.enableAlbumArt && podcast.image.length"
          class="player__image-wrapper"
          :class="{ 'player__image-wrapper--mini': isMini() }"
        >
          <img
            ref="playerImage"
            :src="podcastImage"
            :alt="podcast.name"
            class="player__image"
            :class="{ 'player__image--mini': isMini() }"
            @load="imageLoad"
            @error="imageError"
          />
        </div>
        <play-pause
          v-if="isMini()"
          :is-playing="isPlaying"
          :is-loading="isLoading"
          :show-increments="false"
          :theme="configs.theme"
          @play="playMedia"
          @pause="pauseMedia"
          @decrement="decrement"
          @increment="increment"
        ></play-pause>
      </div>
      <div
        class="player__column"
        :class="{ 'player__column--grow': isClassic() }"
      >
        <div class="player__content align-items-center">
          <player-info
            :is-classic="isClassic()"
            :title="podcast.name"
            :show-title="showTitle"
            :publish-date="displayDate"
            :is-playing="isPlaying"
            :url="podcast.shortUrl"
            :description="podcast.description"
            :theme="configs.theme"
            :show-options="shouldRenderAutoPlaySettings"
            :autoplay-active="hasAutoplay && autoplayActive"
            :is-live-stream="isLiveStream"
            @toggle-autoplay="toggleAutoplay"
            ref="info"
          ></player-info>
          <scrubber
          :is-loading="isLoading"
            :duration="duration"
            :position="seekPos"
            :time-elapsed="timeElapsed"
            :time-remaining="timeRemaining"
            :podcast-id="podcast.id"
            :theme="configs.theme"
            :is-live-stream="isLiveStream"
            @seeking="onSeeking"
          ></scrubber>
          <play-pause
            v-if="isClassic()"
            :is-playing="isPlaying"
            :is-loading="isLoading"
            :show-increments="!isLiveStream"
            :theme="configs.theme"
            @play="playMedia"
            @pause="pauseMedia"
            @decrement="decrement"
            @increment="increment"
          ></play-pause>
          <hash-tag-list
            v-if="
              isClassic() &&
              configs.preferences.enableHashtags &&
              categories.length &&
              !isLiveStream
            "
            :handle-on-press-show-more="handleOnPressShowMore"
            :theme="configs.theme"
            :tags="categories"
            :character-limit="hashtagCharacterLimit"
            :disable-tags="!configs.preferences.enableGallery"
            @search="handleHashtagSearch"
          ></hash-tag-list>
          <div
            v-if="
              isClassic() &&
              configs.preferences.enableHashtags &&
              podcast.categories.length === 0
            "
            class="hash-tag-placeholder"
          ></div>
          <description
            v-if="isClassic() && configs.preferences.enableShowNotes"
            :description="podcast.description"
            :theme="configs.theme"
          ></description>
        </div>
      </div>
    </div>
    <div class="player__content">
      <gallery
        v-if="isClassic() && configs.preferences.enableGallery"
        :items="filteredPodcasts"
        :is-playing="isPlaying"
        :is-loading="isLoading"
        :is-classic="isClassic()"
        :podcast-id="podcast.id"
        :handle-search-click="handleSearchClick"
        :showing-filtered-podcasts="showingFilteredPodcasts"
        :handle-clear-search="handleClearSearch"
        :search-term="searchTerm"
        :theme="configs.theme"
        @load-podcast="loadPodcast"
        @pause="pauseMedia"
        ref="gallery"
      ></gallery>
    </div>
    <audio
      ref="player"
      @timeupdate="onTimeUpdate"
      @canplay="onCanPlay"
      @ended="onEnded"
      @error="onMediaError"
    >
      <source :src="audioSrc" @error="onMediaError" />
      <p>Your browser doesn't support HTML5 audio.</p>
    </audio>
  </div>
</template>

<script>
// 🔴 D-146 — the NotAllowed measurement was v4-ONLY, so a blocked autoplay on every
// other version was invisible in Faro. NotAllowedError is the browser refusing to play
// without a gesture: exactly the failure a listener experiences as "the widget is
// broken" and an operator cannot otherwise see. F-3/MR-69 chose a per-call-site report
// over a global `unhandledrejection` listener PRECISELY because each catch knows its own
// station — and then only one version's call sites got it. Ninth one-of-N here.
//
// `reportNotAllowedIfApplicable` filters on `isNotAllowedRejection` and never throws, so
// it is safe on every rejection path, including the benign AbortError ones.
import { reportNotAllowedIfApplicable, reportPlayerRetry } from '../core/faro';
import Scrubber from './Controls/Scrubber';
import PlayPause from './Controls/PlayPause';
import PlayerInfo from './PlayerInfo';
import HashTagList from './HashTag/HashTagList';
import HashTagModal from './HashTag/Modal';
import Description from './Description';
import Gallery from './Gallery/Gallery';
import { getJson } from '../utils/http';
import { getLiveStreamUrl, urlGetter } from '../utils/utils';
import Search from './Search/Search.vue';
import Modal from './Utils/Modal';
import LiveRegion from '../shared-components/LiveRegion.vue';
// LP-4 / MR-78: live audio routes through the shared hls.js engine (LP-H/MR-75)
// instead of the retired Viblast runtime global.
import { attachLiveStream, detachLiveStream } from '../utils/hls_engine';

export default {
  name: 'v3-player',
  // Vue3 (LP-4/MR-78 emits parity — mirrors the v4 Player sibling): the shell
  // binds `@switch-category` on the v3 player, so declare it as a component event.
  // `image-loaded` is the #399 cover-landed notification (see notifyShellOfResize).
  emits: ['switch-category', 'image-loaded'],
  components: {
    LiveRegion,
    Scrubber,
    PlayPause,
    PlayerInfo,
    Description,
    HashTagList,
    HashTagModal,
    Gallery,
    Search,
    Modal,
  },
  props: {
    podcasts: Array,
    configs: Object,
    apiResourceUrl: String,
    apiPrefix: String,
    categories: Array,
  },
  data() {
    return {
      podcast: this.podcasts[0],
      isPlaying: false,
      // D-235 (A3): what the two live regions are currently saying. See the v4 sibling
      // for why these are data fields rather than computeds over the state they mostly
      // track.
      statusMessage: '',
      failureMessage: '',
      // D-235 (A6): see the v4 sibling. Null so the first play announces the title.
      announcedPodcastId: null,
      seekPos: 0,
      duration: 0,
      timeElapsed: 0,
      timeRemaining: 0,
      mediaReady: false,
      // The failure latch v2 has had since D-133, and its outcome companion.
      // `mediaFailed` says "the load failed"; `retryPending` says "the listener asked
      // for another go and we do not yet know how it went", which is what turns a
      // retry COUNT into a success RATE.
      mediaFailed: false,
      retryPending: false,
      canPlayLiveStream: false,
      liveStreamUri: '',
      audioIsLoaded: false,
      hashTagModalIsVisible: false,
      searchIsVisible: false,
      searchTerm: '',
      showingFilteredPodcasts: false,
      isLoading: false,
      autoplayActive: true,
      // PS-5: initialised from the autoplay mode instead of being set as a side
      // effect of the hasAutoplay computed. configs.autoplay is static for the
      // widget's lifetime, and the v3 template reads hasAutoplay during render, so
      // on the base code dropToLive was already true by the first onEnded — this
      // preserves that observable behaviour while keeping hasAutoplay pure.
      dropToLive: this.configs.autoplay === 'live',
      isLiveStream: false,
      preloadedAudio: [], // podcast ids of audio files already loaded
      showTitle: '',
      imageType: 'image',
    };
  },
  computed: {
    playerClassName() {
      return [`player--${this.configs.mode}`];
    },

    audioSrc() {
      if (this.canPlayLiveStream) {
        return this.liveStreamUri;
      }

      return this.m4aURI;
    },

    m4aURI() {
      return `${this.apiPrefix}${this.podcast.m4aFile}`;
    },

    shouldRenderAutoPlaySettings() {
      return ['next', 'live'].includes(this.configs.autoplay);
    },

    hasAutoplay() {
      // PS-5: pure — the `this.dropToLive = true` side effect moved to data() init
      // (dropToLive is seeded from configs.autoplay === 'live').
      return (
        ['next', 'live'].includes(this.configs.autoplay) && this.autoplayActive
      );
    },

    filteredPodcasts() {
      const searchTerm = this.searchTerm.toLowerCase().trim().replace('#', '');
      const limit = Number(this.configs.preferences.galleryLimit);

      if (!searchTerm) {
        return this.podcasts.slice(0, limit);
      }

      const filtered = [...this.podcasts].filter((podcast) => {
        const { name, categories, description } = podcast;

        // Matches name or description
        const matchesName = name.toLowerCase().includes(searchTerm);
        const matchesDescription = description
          .toLowerCase()
          .includes(searchTerm);
        if (matchesName || matchesDescription) {
          return true;
        }
        // Matches category
        let matchesCategory;
        for (let index = 0; index < categories.length; index++) {
          const category = categories[index];
          if (category.name.toLowerCase().includes(searchTerm)) {
            matchesCategory = true;
            break;
          }
        }

        return matchesCategory;
      });
      return filtered.slice(0, limit);
    },

    themeStyles() {
      return {
        background: this.configs.theme.background,
      };
    },

    hashtagCharacterLimit() {
      const viewportWidth = document.body.offsetWidth;
      const isClassicWithAlbumArt =
        this.isClassic() && this.configs.preferences.enableAlbumArt;

      return isClassicWithAlbumArt && viewportWidth < 670 ? 40 : 90;
    },

    resourceUrlForShowName() {
      if (!this.apiResourceUrl) return '';
      const url = new URL(this.apiResourceUrl);
      const id = this.podcast.id;
      url.search = `?limit=1&page=1&id=${id}`;
      return url.toString();
    },

    podcastImage() {
      // PS-5: pure — just resolves the image URL. The imageType classification
      // (which drives the <img> re-classing watcher) is derived separately, in
      // imageTypeForPodcast(), and assigned from mounted()/the podcast watcher.
      if (this.podcast.squareImage) {
        return urlGetter(this.apiPrefix, this.podcast.squareImage);
      }
      if (this.podcast.shareImage) {
        return urlGetter(this.apiPrefix, this.podcast.shareImage);
      }
      if (this.podcast.originalImage) {
        return urlGetter(this.apiPrefix, this.podcast.originalImage);
      }
      return urlGetter(this.apiPrefix, this.podcast.image);
    },

    displayDate() {
      return this.podcast.widgetPublishDate || this.podcast.timestampCreated;
    },
  },
  watch: {
    // D-235 (A3): announced from WATCHERS, not from the handlers that cause the change —
    // `isPlaying` is written from several places in this file and announcing at each site
    // is how one of them gets missed. 🪤 Nothing here watches anything driven by
    // `timeupdate`: a live region fed by playback position is unusable.
    isPlaying(playing) {
      // Guarded on `isLoading` so the pair "loading -> playing" announces once rather
      // than racing its own "Loading" line. 🔴 D-264 (A6 fix): when a load IS in
      // flight the announcement is DEFERRED to the `isLoading` falling edge below, NOT
      // dropped. `playMedia` sets `isPlaying` true synchronously with `isLoading`, so this
      // watcher sees `isLoading` true here, and `onCanPlay` later clears `isLoading`
      // WITHOUT moving `isPlaying` -- so this watcher never re-fires for a cold play or an
      // autoplay-next. Announcing only from here left both silent (the region said just
      // "Loading" and never the title); the falling edge is where those two paths land.
      if (this.isLoading) return;
      this.announcePlaybackState(playing);
    },
    isLoading(loading) {
      if (loading) {
        this.announce('Loading');
        return;
      }
      // D-264 (A6 fix): the load just completed. `isPlaying` was already set true, in the
      // same tick as `isLoading`, so the watcher above early-returned; announce the
      // resolved state HERE. Only for a real play -- `onMediaError` also clears
      // `isLoading`, and it owns the `role="alert"` failure channel, so a failed load must
      // not also speak on the polite one (by then `mediaFailed` is true and `isPlaying`
      // false, so this guard blocks it).
      if (this.isPlaying && !this.mediaFailed) this.announcePlaybackState(true);
    },
    // D-235 (A6): filter results. 🔑 A watcher rather than the four call sites is not a
    // preference here — `searchTerm` is assigned from FOUR places in this file
    // (`handleHashtagSearch` twice, `handleClearSearch`, `handleOnPressSearch`), which
    // is precisely A3's argument for watchers: announcing at each site is how one of
    // them gets missed.
    searchTerm() {
      this.announceFilterResults();
    },
    mediaFailed(failed) {
      // Cleared as well as set: AT announces the DIFF, so a stale line means the next
      // failure announces nothing.
      this.failureMessage = failed ? 'This episode could not be played' : '';
    },
    // PS-5: keep imageType in sync with the current podcast. It used to be written
    // as a side effect of the podcastImage computed (fired on render); now it's
    // derived here (on episode switch) and in mounted() (initial), so the computed
    // stays pure and the observable <img> class is unchanged.
    podcast() {
      this.imageType = this.imageTypeForPodcast();
    },
    imageType(type) {
      // PS-7: scope the lookup to this component's own <img> (was a global
      // document.querySelector reaching into the whole page) and null-guard it —
      // the element is absent when album art is disabled or the component is
      // detached, where the old code threw on `null.classList`.
      const img = this.$refs.playerImage;
      if (!img) return;
      img.classList = 'player__image';

      if (type === 'square') {
        img.classList.add('square_image');
      } else {
        img.classList.add('rectangle_image');
      }
    },
  },
  methods: {
    // D-235 (A3): put a line through the polite channel. 🪤 The empty-string reset is
    // load-bearing — AT announces the DIFF, so pressing Pause twice would announce once
    // and then go silent. See the v4 sibling.
    // D-235 (A6) / D-264: announce play/pause state, NAMING the episode on the first
    // play or an episode switch and saying "Playing"/"Paused" on a resume of the one
    // already announced. Called from the `isPlaying` watcher on the resume/pause paths,
    // and from the `isLoading` falling edge on the cold-play and autoplay-next paths,
    // where `isPlaying` was already true before the load finished.
    //
    // 🚤 Announced from HERE rather than from a `podcast` watcher, and the
    // placement is the whole design. A `podcast` watcher fires in the MIDDLE of
    // `loadPodcast` (`pauseMedia()` -> assign `podcast` -> `updatePlayer()` ->
    // `playMedia()`), so an episode switch would announce "Paused", the title, "Loading",
    // "Playing" -- four lines for one action, the title said while the element is still at
    // readyState 0.
    //
    // 🔑 It REPLACES the generic line rather than adding to it. Resuming the same
    // episode after a pause still says "Playing"; `announcedPodcastId` distinguishes the
    // two and is seeded null so the FIRST play does name the episode. The autoplay-next
    // transition -- playback moving to a new episode with no user action -- falls out of
    // the same id check, and it is the case the plan singled out as sharpest.
    announcePlaybackState(playing) {
      if (playing && this.podcast && this.podcast.id !== this.announcedPodcastId) {
        this.announcedPodcastId = this.podcast.id;
        this.announce(`Now playing: ${this.podcast.name || 'episode'}`);
        return;
      }
      this.announce(playing ? 'Playing' : 'Paused');
    },

    announce(message) {
      this.statusMessage = '';
      this.$nextTick(() => {
        this.statusMessage = message;
      });
    },

    /**
     * D-235 (A6). What the filtered gallery now contains.
     *
     * v3 already shows "Search results for X" (`Gallery/SearchActionsBar`), so the
     * TERM is visible; what is not visible anywhere is how many results it found. The
     * announcement therefore carries the count, and matches the list the user is
     * looking at rather than a separate total — v4's sibling has a note on why that
     * distinction mattered there.
     */
    announceFilterResults() {
      const term = this.searchTerm.trim();
      const count = this.filteredPodcasts.length;

      if (!term) {
        this.announce(`Showing all ${count} episodes`);
        return;
      }

      if (count === 0) {
        this.announce(`No episodes match ${term}`);
        return;
      }

      this.announce(`${count} episode${count === 1 ? '' : 's'} match ${term}`);
    },

    playMedia() {
      // 🔑 A tap after a failed load is a RETRY, not a play. `play()` on a dead
      // element rejects and changes nothing, so without this branch the control
      // looks alive and fails identically every time — harder to diagnose than v2's
      // old inertness, not easier.
      if (this.mediaFailed) {
        this.mediaFailed = false;
        this.retryPending = true;
        this.reportRetry('attempted');
        // The LIVE path starts playback itself, inside the engine — nothing further
        // to do here.
        if (this.retryLoad()) return;
        // The m4a path has only re-pointed and re-loaded the element, so it FALLS
        // THROUGH to the ordinary play below rather than stopping at the reload.
        //
        // 🔑 That is not a convenience. A tap on the play control is a request for
        // AUDIO, and a retry that reloads and then sits there makes the listener tap
        // twice for no reason they can see — the live branch above would already have
        // played. Falling through also means the retry reuses the rejection handling,
        // the `isLoading` bookkeeping and the NotAllowed reporting that the ordinary
        // path already has, instead of growing a second copy of all three.
        //
        // 🪤 MEASURED, and it is why this shape exists at all: with the retry
        // returning here, `autoplay-rejection.spec.js` (D-207) went RED in four
        // places. `stubAudio`'s fixture genuinely fails to decode in chromium, so
        // `mediaFailed` is already true when the spec clicks — the click became a
        // retry, `play()` was never called, and the denial that spec exists to
        // measure never happened. The probe that settled it recorded the pre-click
        // error as `{tag:'SOURCE', hasSrcAttr:true}`, which also refuted my first
        // hypothesis (a src-less `<source>`, the guard v2 carries) before I shipped
        // a fix for the wrong cause.
      }
      if (!this.preloadedAudio.includes(this.podcast.id)) {
        this.isLoading = true;
      }

      if (this.canPlayLiveStream && !this.isPlaying) {
        // if live stream paused and play is clicked, original playlist should start again
        this.canPlayLiveStream = false;
        this.isLiveStream = false;
        const forceUpdate = true;
        this.loadPodcast(this.podcast.id, forceUpdate);
      }

      if (!this.isPlaying) {
        const player = this.$refs.player;
        const playPromise = player.play();

        // Backwards compatibility for autoplay
        if (playPromise !== undefined) {
          // 🔴 D-203/D-207 — thenable-safe, WITHOUT collapsing to `.then(f, h)`.
          // Same fix and same reasoning as v4-components/Player.vue; see the long
          // note there. Short version: this read `.catch` off whatever `play()`
          // returned, a host-patched thenable has only `.then`, and reading `.catch`
          // off one throws synchronously inside the click handler at the moment
          // playback has already failed. Wrap first — the chained semantics (h also
          // catches a throw from f) are kept on purpose.
          Promise.resolve(playPromise)
            .then(() => {
              // play back successful
            })
            // eslint-disable-next-line no-console
            .then(null, (error) => {
              // D-146 — v3 reports the NotAllowed measurement too.
              reportNotAllowedIfApplicable(error, this.podcast && this.podcast.station);
              console.error(error);
              // D-131 — clear the load state on a REJECTED play, or the spinner is
              // permanent. `playMedia` raises `isLoading` for a non-preloaded episode;
              // if `play()` then rejects, the element dispatches NO new `error`
              // (NotSupportedError on an already-errored element, NotAllowedError on a
              // blocked autoplay), so neither `onMediaError` nor `onCanPlay` can ever
              // run and nothing else clears it.
              //
              // 🪤 This is what made D-105's fix a ONE-SHOT: it restored the play
              // button, and the first click on that button re-wedged the spinner for
              // good — measured in chromium, loaders 0 -> 1 with no new error event.
              // The widget was advertising the control that reached the dead end.
              // 🔴 D-137 — an AbortError here is BENIGN and must NOT be treated as a
              // media failure. `loadPodcast -> pauseMedia() -> player.pause()` rejects
              // the still-pending `play()` on EVERY episode switch made while the
              // previous episode is buffering, and D-131 routed that into
              // `onMediaError()` — clearing `isPlaying`/`isLoading`/`mediaReady` for the
              // episode the listener ABANDONED, which corrupts the state of the one now
              // playing.
              //
              // MEASURED end-to-end in chromium against the widget's own UI: audio
              // `paused:false, currentTime advancing, readyState 4` while the transport
              // read **"Play"**. Neither Player binds the native `play` event, so
              // nothing repairs `isPlaying` — the wrong value LATCHES until the next
              // user action and pausing then takes two taps.
              //
              // 🪤 The repo already documents this rejection as routinely benign in the
              // two places it deliberately swallows it (podcast.vue's `onSeeking`:
              // "pause is interrupting the play at times"; v3 `playLiveStream`:
              // 'Play call interrupted'). D-131 read the same event as a failure.
              if (error && error.name === 'AbortError') return;
              this.onMediaError();
            });
        }
      }
      this.isPlaying = true;
    },

    /**
     * Pause this media in particular.
     */
    pauseMedia() {
      if (this.$refs.player) {
        this.$refs.player.pause();
      }

      this.isPlaying = false;
    },

    /**
     * Updates the audio players if a new podcast ID is provided then plays the media
     *
     * @param {Number} id the podcast id
     * @param {Boolean} forceUpdate force update the audio element source, default is false
     * @return {void}
     */
    loadPodcast(id, forceUpdate = false) {
      this.pauseMedia();
      // 🪤 THE D-138 SHAPE, pre-empted. A latch armed once and never cleared stays
      // armed for the life of the component: the next episode would render as
      // failed and its FIRST tap would be misrouted into the retry branch. Cleared
      // here — the episode-switch entry point — and NOT in `resetTimeline()`, which
      // `updatePlayer()` also calls from inside the retry itself and would therefore
      // cancel the very retry it is meant to be tracking.
      this.mediaFailed = false;
      this.retryPending = false;
      if ((id && id !== this.podcast.id) || forceUpdate) {
        this.podcast = this.podcasts.find((podcast) => podcast.id === id);
        this.updatePlayer();
      }
      this.setShowTitle();
      this.playMedia();
      // player-info is only rendered in classic mode; guard like v4 does.
      if (this.$refs.info) this.$refs.info.scrollTitle();
    },

    updatePlayer() {
      const audioPlayer = this.$refs.player;
      audioPlayer.removeAttribute('src');

      // D-103 — the guard that used to wrap this block was DEAD: `removeAttribute`
      // one line above leaves the reflected `src` IDL attribute empty, so its first
      // clause `!audioPlayer.src` was unconditionally true and the live-vs-m4a
      // clause could never be evaluated. VERIFIED in chromium, not assumed:
      // setAttribute -> 'https://host/media/x.m4a', removeAttribute -> '' (falsy).
      //
      // Deleted rather than rewritten. No commit or comment records what the
      // live-vs-m4a branch was FOR, and reconstructing an intent from a condition
      // that has never once evaluated would be fiction. Deleting is provably
      // behaviour-preserving; guessing is not. Pinned by
      // test/unit-vue/update-player-dead-condition.spec.js, which also pins the
      // PREMISE (src is falsy after removeAttribute) so the deletion's own
      // justification fails loudly if an engine ever changes it.
      // Detach the live engine (hls.js, LP-H/MR-75). detachLiveStream is
      // idempotent + throw-safe (it swallows the iOS quirk the old guarded
      // `viblast(el).stop()` was try/caught for), so the src-swap below runs
      // regardless of whether a live session was attached. (Was the guarded
      // Viblast stop call on the runtime global, migrated to hls.js.)
      detachLiveStream(audioPlayer);
      audioPlayer.setAttribute('src', this.audioSrc);
      audioPlayer.load();
      // load() puts the element back at readyState 0 with duration NaN, so every
      // Vue-data mirror of its timeline is now a lie and the controls cannot be
      // trusted until `canplay`. INSIDE the branch on purpose: where the src is
      // not swapped the element keeps its state and the mirrors stay valid
      // (D-97/D-98).
      this.resetTimeline();
    },

    /**
     * Forget everything mirrored from the media element and re-open the load
     * window. Called only where the element is actually reloaded.
     *
     * `isLoading` is set here rather than left to `playMedia`, which sets it only
     * `if (!this.preloadedAudio.includes(this.podcast.id))` — a cache that cannot
     * vouch for an element reset a line earlier. That hole kept the load window
     * fully reachable for any RE-selected episode: no loader, live controls, and an
     * element at readyState 0 (D-98).
     *
     * `mediaReady` has no readers today, but it is written by `onCanPlay` and was
     * never cleared, so it is reset with the rest rather than left as a third
     * variant of the same defect (see D-94 on `duration`).
     *
     * @return {void}
     */
    resetTimeline() {
      this.duration = 0;
      this.timeElapsed = 0;
      // NOT zero — this mirror is the one thing here that IS knowable at
      // readyState 0. `mounted()` seeds it from parseInt(podcast.duration), the
      // playlist metadata, and `loadPodcast` sets `this.podcast` to the newly
      // chosen episode BEFORE calling updatePlayer() — so the right answer is
      // already in hand. Zeroing it made the scrubber advertise every freshly
      // selected episode as 00:00 long for the whole window this reset opens.
      // Caught by review, not by a gate — and the reason is NOT masking, which is
      // what an earlier version of this comment claimed. MEASURED 2026-09-03 LOCAL
      // across the 201 goldens: 78 carry NO mask at all, 102 carry one rect, 21
      // carry two. (It read "the 162 goldens: 60 / 84 / 18" until then — a LIVE
      // figure from the 24-cell era, re-derived rather than frozen because it is
      // offered as present evidence for a present claim, and the claim survives
      // it: most goldens still carry no mask, so masking is still not the reason.)
      // The real
      // reason is that no captured state is INSIDE the load window — the `play` cells
      // screenshot after `canplay` — so the visual suite never renders this label
      // while it is wrong, mask or no mask.
      this.timeRemaining = this.metadataDuration();
      this.seekPos = 0;
      this.mediaReady = false;
      this.isLoading = true;
    },

    playLiveStream() {
      this.isLiveStream = true;
      getLiveStreamUrl(this.podcast.station)
        .then((url) => {
          this.liveStreamUri = url;
          this.canPlayLiveStream = true;
          // 🔴 D-153 — guard the ref. `attachLiveStream` already returns early on a
          // falsy element (the documented MED-5 fix, added for exactly this race) and
          // then the very next line dereferenced the same ref. Every caller runs
          // inside a `getLiveStreamUrl(...).then(...)`, so an unmount while that fetch
          // is in flight leaves `$refs.player` cleared: `.play()` threw a TypeError
          // inside the `.then`, the chained OUTER `.catch` swallowed it, and it logged
          // 'Unable to fetch live stream' — for a fetch that SUCCEEDED. An operator
          // reads that and goes hunting a CDN that answered fine.
          //
          // Returning here also stops the writes below from landing on a dead
          // component, which is what MED-5 established as the right handling for an
          // unmount race: it is not an engine failure and must not be reported as one.
          // 🔑 The population is DERIVED, not typed: this guard's own wording ("Every
          // caller…") was correct while the fix reached only 2 of the 3 callers —
          // v1 `podcast.vue` was missed and a two-member `describe.each` could not
          // see it. `test/unit-vue/live-fetch-unmount-population.spec.js` now derives
          // the set from src/ and requires every member to be classified.
          const player = this.$refs.player;
          if (!player) return;
          // 🔴 D-147 — recorded BEFORE the attach, and the ORDER is the entire fix.
          //
          // `notifyUnrecoverable` dispatches a real `error` event and `dispatchEvent`
          // is SYNCHRONOUS, so on the two branches D-143 added (`no_engine`,
          // `attach_threw`) the give-up runs to completion inside `setupHlsEngine()`
          // below — `@error` -> `onMediaError` -> `isPlaying = false`. With this
          // assignment sitting after that call, it overwrote the repair two
          // statements later and the transport went back to reading PAUSE with
          // nothing playing. Nothing else could fix it: no native `play` binding, and
          // `onCanPlay` cannot fire when nothing was attached. That is D-142's defect,
          // reopened on the exact path D-143 exists to signal.
          //
          // 🪤 WHICH ENGINES — measured, because an earlier revision of this comment
          // said flatly "`no_engine` is the SHIPPED off-master state (!207)", and that
          // names the wrong branch for the majority engine. `canPlayType` for the HLS
          // MIME, measured in this repo's own playwright browsers: chromium -> 'maybe'
          // (so it takes the NATIVE branch and `no_engine` is UNREACHABLE there),
          // firefox -> '' (so it takes `no_engine`). Off-master the dead-engine path is
          // therefore `no_engine` on FIREFOX; on Chrome the failure arrives as an
          // ASYNCHRONOUS native `error`, which lands after this assignment and so was
          // never clobbered by the old order. `hls_engine.js:553-560` has recorded this
          // since 2026-08-06 — it is the correction PS-22/PS-23 needed, and the comment
          // it corrects is the one I had just written.
          //
          // 🪤 The comment that used to sit at the old position argued this assignment
          // was safe because "the rejection settles a microtask LATER, so onMediaError
          // above already overwrites this". That is true of the `play()` rejection and
          // FALSE of the engine notify, which lands first and synchronously. A
          // justification that only considered one of the two writers.
          //
          // Nothing observable changes on the healthy path: `play()` is async, so this
          // flag always led actual playback regardless of which side of the attach it
          // sat on.
          this.isPlaying = true;
          this.setupHlsEngine();
          player
            .play()
            // eslint-disable-next-line no-console
            // 🔴 D-176 — `.then(null, handler)`, not `.catch(handler)`: a host page that patches
            // `play()` (ad blockers, some CMPs) can return a THENABLE with no `.catch`, and
            // calling it throws TypeError at the moment playback already failed. `.then` is the
            // only method a thenable must have. Same contract as startPlayback in hls_engine.js.
            .then(null, (err) => {
              // D-146 — the live path reports too. v4 had this; v3 did not.
              reportNotAllowedIfApplicable(err, this.podcast && this.podcast.station);
              console.warn('Play call interrupted', err);
              // 🔴 D-142 — repair the state, don't just log it. This is the OTHER
              // `play()` call site: D-131/D-137 fixed `playMedia` and this one kept
              // the defect. `this.isPlaying = true` below runs unconditionally, so a
              // rejected live play left the transport rendering PAUSE with nothing
              // playing — and neither Player binds the native `play` event, so the
              // wrong value LATCHES until the listener taps the control, which then
              // pauses media that never started.
              //
              // 🪤 The stuck SPINNER that the on-demand side suffers is NOT the main
              // harm here, and the first write-up of this finding had that wrong.
              // `playLiveStream` never raises `isLoading`, there is no live button in
              // the template, and its one caller — `onEnded` under autoplay=live —
              // runs after `onCanPlay` has already cleared it. `isLoading` is stuck
              // only in the select-during-live-fetch race (episode B raises the
              // spinner via `resetTimeline` while `getLiveStreamUrl` is still in
              // flight). `onMediaError` covers that too, so both are closed here.
              //
              // AbortError is excluded for exactly D-137's reason, reached by the
              // other site: `pauseMedia()` rejects a still-pending live `play()`
              // whenever an episode is chosen while the live stream is starting, and
              // by the time that rejection lands the state describes the episode now
              // playing, not the abandoned live attempt.
              if (err && err.name === 'AbortError') return;
              this.onMediaError();
            });
        })
        // eslint-disable-next-line no-console
        .catch((e) => {
          this.isLiveStream = false;
          console.warn('Unable to fetch live stream', e);
        });
    },

    // 🔑 THE RETRY TAKES THE SAME BRANCH THE SETUP TOOK, and reuses the setup paths
    // that already exist rather than growing a third way to point an element at a
    // source. `updatePlayer()` is the m4a path (detach, set src, load, reset the
    // timeline) and `setupHlsEngine()` is the live one; a retry that re-implemented
    // either would be free to drift from it, which is exactly how v1 and v2 ended up
    // with OPPOSITE recovery behaviour on opposite paths — v1's HLS retry worked and
    // its m4a retry did not, v2's m4a retry worked and its HLS retry did not, and
    // neither component had a rule. Both had whatever fell out of the bookkeeping.
    //
    // 🔑 THE DISCRIMINATOR IS `canPlayLiveStream`, NOT v2's `useM4a`. `audioSrc` here
    // is `canPlayLiveStream ? liveStreamUri : m4aURI` — v3/v4 episodes are ALWAYS
    // m4a and only the live stream is HLS.
    // 🪤 The reason given here used to be "v2's gate is `useM4a` because a default v2
    // embed serves on-demand episodes as `.m3u8` through hls.js". That is FALSE and
    // the correction is D-220 (MEASURED 2026-09-06): `app.vue` binds the literal
    // `:use-m4a="true"` on its `<v2-player>` mount, so `NowPlayingSection.useM4a` is
    // always true, its `m3u8Url` branch is unreachable, and v2's on-demand episodes
    // are m4a exactly like v3/v4's. v2 is not a different POPULATION.
    // What is different is the INSTRUMENT: v2's gate is a sniff of the src string
    // (`LiveAudioPlayer`'s own computed). v3/v4 read the flag directly instead.
    // 🪤 This used to say `updatePlayer()` and `setupHlsEngine()` are "already keyed on
    // `canPlayLiveStream`, and the retry reuses them". MEASURED FALSE on both counts:
    // neither method reads the flag (0 hits in either body; `retryLoad` itself is the
    // only reader), and `retryLoad()` reuses `updatePlayer()` for the m4a path but NOT
    // `setupHlsEngine()` for the live one — it re-implements that attach inline so it
    // can pass `autoplay: true`, which is deliberate and explained at the call site.
    // What actually makes the flag safe here is that `audioSrc` is DERIVED from it
    // (`canPlayLiveStream ? liveStreamUri : m4aURI`), so reading `canPlayLiveStream`
    // asks the same question the src answers — the very property that makes v2's
    // src-sniff correct. The two gates differ in INSTRUMENT, not in what they ask.
    // (v2 also has TWO attach sites today, not one: `retryLoad()` and the
    // `audioFileSrc` watcher, both in `LiveAudioPlayer`.)
    // Returns TRUE when it has fully handled the retry (the live path, which starts
    // playback itself) and FALSE when the caller must still play (the m4a path).
    retryLoad() {
      const player = this.$refs.player;
      if (!player) return true;
      if (this.canPlayLiveStream) {
        // Stop-before-(re)attach, as every other attach site here does.
        detachLiveStream(player);
        // 🔑 `autoplay: true`, and ONLY on the retry. `setupHlsEngine()` passes
        // false because its caller plays explicitly right after; this path is a
        // click on the play control, so the listener has already asked for audio and
        // a re-attach that left silence would make them tap again for no visible
        // reason. A rejected `play()` is caught and reported inside the engine
        // (D-151/D-207), so the optimistic call cannot surface as an unhandled
        // rejection.
        try {
          attachLiveStream(player, this.audioSrc, { autoplay: true });
        } catch (e) {
          // Parity with setupHlsEngine's belt-and-braces catch; the engine is
          // already throw-safe.
        }
        return true;
      }
      // m4a: re-point and re-run resource selection. `updatePlayer()` ends in
      // `removeAttribute`/`setAttribute`/`load()`, which is precisely what an element
      // sitting in error state needs — and unlike a bare `play()` it actually issues
      // a request.
      this.updatePlayer();
      return false;
    },

    // Reported through the SHARED reporter in `core/faro.js`: v2, v3 and v4 all emit
    // this event, and three inline copies of an attribute set is where a schema
    // drifts. `reportNotAllowedIfApplicable` — which shipped v4-ONLY and was the
    // ninth one-of-N defect here — is why that file is where a cross-version
    // reporter lives.
    reportRetry(phase) {
      reportPlayerRetry(phase, {
        playerVersion: 'v3',
        sourceKind: this.canPlayLiveStream ? 'hls' : 'm4a',
        live: this.canPlayLiveStream,
      });
    },

    // Settle a pending retry exactly once, whichever way it went.
    settleRetry(phase) {
      if (!this.retryPending) return;
      this.retryPending = false;
      this.reportRetry(phase);
    },

    setupHlsEngine() {
      // Attach the live HLS stream via the shared hls.js engine (LP-H/MR-75),
      // replacing the old Viblast stop()+setup() calls. attachLiveStream does its
      // own stop-before-attach (it tears down any prior session first, as the old
      // viblast(el).stop() always preceded setup()). autoplay:false keeps the
      // Viblast contract — playLiveStream calls this.$refs.player.play() explicitly
      // right after. The `key` (Viblast license) + `forceHEAAC` drop — no hls.js
      // equivalent (HE-AAC is handled by hls.js's transmuxer). attachLiveStream is
      // already throw-safe (it swallows the iOS quirk the old setup was try/caught
      // for); the try/catch is kept for belt-and-suspenders parity.
      try {
        attachLiveStream(this.$refs.player, this.audioSrc, { autoplay: false });
      } catch (e) {
        // swallowed — engine is throw-safe; see method note
      }
    },

    /**
     * The episode length as the PLAYLIST reports it, which is knowable before the
     * element has any metadata of its own. `|| 0` because a missing or
     * non-numeric duration is a real condition in this data and parseInt would
     * otherwise put NaN straight into a rendered time label.
     *
     * @return {number} seconds, or 0
     */
    metadataDuration() {
      return parseInt(this.podcast.duration, 10) || 0;
    },

    onTimeUpdate(event) {
      const currentTime = parseInt(event.target.currentTime, 10);
      const duration = parseInt(event.target.duration, 10);

      // Update the slider value to either new seek time or currentTime
      this.seekPos = (100 / (duration || 1)) * currentTime;
      this.timeElapsed = currentTime;
      // 🪤 `load()` QUEUES a timeupdate that fires at readyState 0 with duration
      // NaN, and it lands ONE TICK AFTER resetTimeline()'s re-seed — so without
      // this fallback that event overwrote the re-seed with (NaN || 0) - 0 = 0 and
      // the whole re-seed was inert. MEASURED in three real engines: chromium
      // fires it, webkit and firefox do not. So the bug reproduced only in Blink,
      // the engine most listeners use, and happy-dom cannot see it at all — it
      // queues no timeupdate on load() (D-104).
      // Fall back to the episode metadata, the same source resetTimeline seeds
      // from. A live stream reports Infinity and lands here too; its label is
      // hidden (`v-if="showTime && !isLiveStream"`), and seekPos/timeElapsed above
      // keep updating either way.
      this.timeRemaining = Number.isFinite(event.target.duration)
        ? duration - currentTime
        : this.metadataDuration();
    },

    onCanPlay(event) {
      this.duration = event.target.duration;
      this.mediaReady = true;
      this.isLoading = false;
      this.preloadedAudio.push(this.podcast.id);
      this.settleRetry('recovered');
    },

    /**
     * D-105 — a failed load must return the play button, not spin forever.
     *
     * `isLoading` was cleared in exactly one place, `onCanPlay`, so any media that
     * never reaches `canplay` (404, CORS, decode failure) left the Loader sitting
     * where the play button belongs for the rest of the pageview.
     *
     * 🪤 Bound on BOTH the media element and the `<source>` child, and that is not
     * belt-and-braces. MEASURED in chromium: `setAttribute('src', bad)` — the
     * `updatePlayer()` episode-swap path — fires `error` at the MEDIA element with
     * `el.error.code = 4`, while a failing `<source>` child — what the template
     * renders on FIRST load — fires `error` at the SOURCE, which does not bubble,
     * leaving `el.error` null. Binding only `<audio>` would have fixed the swap
     * and left first load, the commoner failure, spinning.
     *
     * Takes no argument on purpose: `event.target.error` is null on the source
     * path, so reading it would work in testing and be useless in production.
     */
    /**
     * 🔑 D-160 — `isLiveStream` and `canPlayLiveStream` are deliberately NOT cleared
     * here, and that is a MEASURED decision rather than the omission it looks like.
     *
     * They were part of the derived population D-160 raised ("what state means
     * LIVE-IN-PROGRESS, and does the give-up clear it?"), so clearing them looks like
     * the obvious completion of the fix. Measured consequence of doing it, with the
     * live state established and `onMediaError()` called:
     *
     *   audioSrc BEFORE  https://edge.streamon.fm/hls/TEST.m3u8
     *   audioSrc AFTER   https://api.example/a.m4a
     *
     * `audioSrc` is `canPlayLiveStream ? liveStreamUri : m4aURI`, so clearing the flag
     * rewrites `<source :src="audioSrc">` — silently moving the listener off live and
     * back onto the episode that just finished. That is a CONTENT change on every
     * media error, and `@error` is bound on both the element and the `<source>`, so
     * transient failures reach it too.
     *
     * The exit already exists and is deliberate: `playMedia` clears both flags and
     * calls `loadPodcast` when the listener presses Play on a paused live stream. So
     * the post-give-up state here is a usable control (pinned by
     * `test/e2e/live-giveup-recovers-control.spec.js`) beside live chrome that stays
     * until the next tap — cosmetic and self-repairing, unlike v2's permanent label
     * lie, which is the half D-160 actually fixed.
     *
     * ⚠️ The whole unit suite passes with those two lines ADDED, so
     * nothing gates this either way. Recorded here because the next reader will have
     * the same idea and the measurement is not cheap to redo.
     */
    onMediaError() {
      this.isLoading = false;
      this.mediaReady = false;
      this.isPlaying = false;
      // 🔴 v2 has had this latch since D-133; v3/v4 never got one. Without it a
      // failed load is indistinguishable from "not loaded yet", so the next tap
      // takes the ordinary play path and `play()` rejects on an element that is
      // either in error state (m4a) or has had its engine torn down (live) — every
      // time, silently, for the rest of the pageview. The three clears above are
      // what the give-up used to be, in full.
      this.mediaFailed = true;
      // If this failure followed a retry, it IS that retry's outcome. Reported last,
      // after the state above is settled, so a throw in telemetry could never cost
      // the component its give-up handling.
      this.settleRetry('failed');
    },

    onEnded() {
      this.pauseMedia();

      if (this.dropToLive) {
        this.playLiveStream();
      } else if (this.hasAutoplay) {
        const currentPodIndex = this.filteredPodcasts.findIndex(
          (x) => x.id === this.podcast.id
        );
        const nextPod = this.filteredPodcasts[currentPodIndex + 1];

        if (nextPod && nextPod.id) {
          this.loadPodcast(nextPod.id);
          return;
        }
      }
    },

    /**
     * Event emitted when seeking through audio.
     * @param pos
     */
    onSeeking(pos) {
      const player = this.$refs.player;
      // duration is NaN until metadata arrives and Infinity on a live stream, and
      // currentTime is a WebIDL *restricted* double — a non-finite assignment is a
      // TypeError, not a clamp. The scrubber is enabled through that whole window,
      // so drop the seek rather than throw out of the event handler (D-93).
      if (!Number.isFinite(player.duration)) return;
      const seekTime = player.duration * (pos / 100);
      player.currentTime = seekTime;
    },

    /**
     * Decrements the audio player
     *
     * @param {number} value the amount of seconds to decrement
     * @return {void}
     */
    decrement(value) {
      // Reset to 0 if the decrement value is greater than the current time
      const player = this.$refs.player;
      if (player.currentTime <= value) {
        player.currentTime = 0;
      } else {
        player.currentTime -= value;
      }
    },

    /**
     * Increments the audio player
     *
     * @param {number} value the amount of seconds to increment
     * @return {void}
     */
    increment(value) {
      // Set the currentTime to the duration if the episode would end in <= 30 seconds
      const player = this.$refs.player;
      // D-87: read the ELEMENT's duration, not the `this.duration` data cache.
      // That cache is written ONLY by onCanPlay and is never reset when the
      // source changes, so between `loadPodcast` -> `updatePlayer()` (which does
      // removeAttribute('src') / setAttribute('src') / load(), resetting the
      // element to currentTime 0 and duration NaN) and the next `canplay`, it
      // still holds the PREVIOUS episode's length. The skip buttons are not
      // gated on `isLoading`, so a tap in that window compared a stale number
      // and then seeked the NEW episode to the OLD one's duration.
      //
      // This makes `increment` consistent with its neighbours rather than
      // introducing a convention: `onSeeking` and `onTimeUpdate` above already
      // read the element (`player.duration` / `event.target.duration`).
      // `decrement` is deliberately untouched — it branches on currentTime
      // alone and never consulted duration.
      const duration = player.duration;
      // ⚠️ In the load window there is no known duration, and a skip must do
      // NOTHING. An earlier revision of this fix advanced relatively here, on the
      // stated reasoning that "the element clamps it to the seekable range". That
      // was never measured and is FALSE: with no source loaded there is no
      // seekable range, readyState is HAVE_NOTHING, and `currentTime += value`
      // accumulates into the default-playback-start-position. MEASURED in
      // chromium AND webkit — three taps gave 30, 60, 90. On a 45 s episode that
      // lands past the end, so when metadata finally arrives the element clamps
      // to duration, fires `ended`, and autoplay-next skips the very episode the
      // listener just chose.
      //
      // That was strictly worse than the D-87 bug it replaced: the stale-cache
      // read at least LATCHED (measured: 6, 6, 6 — bounded and idempotent).
      // Returning early is bounded, idempotent, and loses nothing a user wants —
      // the tap simply does not act until the episode is ready.
      if (!Number.isFinite(duration)) return;
      if (duration - player.currentTime <= value) {
        player.currentTime = duration;
      } else {
        player.currentTime += value;
      }
    },

    handleOnPressShowMore() {
      this.hashTagModalIsVisible = !this.hashTagModalIsVisible;
    },

    isMini() {
      return this.configs.mode === 'mini';
    },

    isClassic() {
      return this.configs.mode === 'classic';
    },

    handleSearchClick() {
      this.searchIsVisible = true;
      this.$nextTick(() => {
        // Vue1 reach-through `this.$refs.search.$els.searchInput` is gone in
        // Vue3 (child `$els` no longer exists). Search exposes a `focusInput()`
        // method instead of the parent poking the child's internals.
        if (this.$refs.search) this.$refs.search.focusInput();
      });
    },

    handleOnPressSearch(_searchTerm) {
      if (typeof _searchTerm === 'string') {
        if (_searchTerm.length > 0) {
          this.searchTerm = _searchTerm;
          this.searchIsVisible = false;

          this.$emit('switch-category', _searchTerm);

          this.showingFilteredPodcasts = true;
          // Guarded identically to the object-branch below — a not-yet-mounted
          // gallery can't null-deref (both reach-ins reset to page 1 on search).
          if (this.$refs.gallery) this.$refs.gallery.currentPage = 0;
        }
      } else if (_searchTerm) {
        this.searchTerm = _searchTerm.name;
        this.searchIsVisible = false;

        this.$emit('switch-category', _searchTerm);

        this.showingFilteredPodcasts = true;
        // Reset the gallery to page 1 on a new search. Reaching into the child
        // instance (Vue1 `v-ref:gallery` -> Vue3 `ref="gallery"`) still works in
        // the Options API; guarded so a not-yet-mounted gallery can't null-deref.
        if (this.$refs.gallery) this.$refs.gallery.currentPage = 0;
      }
    },

    handleClearSearch() {
      this.showingFilteredPodcasts = false;
      this.searchTerm = '';
    },

    handleOnChangeSearchTerm(e) {
      this.searchTerm = e;
    },

    imageTypeForPodcast() {
      // PS-5: the image-type classification the podcastImage computed used to
      // assign as a side effect. Same precedence (square > share > original >
      // image). Consumed by mounted() + the podcast watcher to set imageType.
      if (this.podcast.squareImage) return 'square';
      if (this.podcast.shareImage) return 'share';
      if (this.podcast.originalImage) return 'original';
      return 'image';
    },

    // #399 / PS-28 residual — tell the shell the cover changed the layout, so it
    // re-posts the height to the embedder. The contract v1 has always had
    // (podcast.vue → podcast_list → app.imageLoaded) and that !209 gave v4 and v2.
    //
    // v3 is the version that did NOT clip, and the reason is CSS, not wiring:
    // `&__image-wrapper` reserves its box up front (`height: 0;
    // padding-bottom: 100%` on desktop, explicit `height: 240px` under
    // max-width 480), so the cover lands into space that was already allocated
    // and the document height never moves. Measured 535/535 with a 390px cover.
    //
    // That makes v3 safe by ACCIDENT. The change that produced v4's 221px clip
    // was exactly this: R5 swapped a fixed cover box for `height: auto`. If
    // anyone does the same here — or the `--mini` / gallery variants grow an
    // intrinsic box — v3 clips with nothing to catch it, because there was no
    // notification at all. Wiring it costs one emit and removes the dependency
    // on the reservation staying true. The extra post is free: informResize is
    // debounced and de-dupes an identical message.
    //
    // $nextTick so the load has actually rendered before the shell re-measures.
    notifyShellOfResize() {
      this.$nextTick(() => this.$emit('image-loaded'));
    },
    imageLoad() {
      this.notifyShellOfResize();
    },
    imageError() {
      // A broken cover collapses the box on any build where the space is not
      // reserved, so the error path notifies too.
      this.notifyShellOfResize();
    },

    toggleAutoplay() {
      this.autoplayActive = !this.autoplayActive;
    },

    retrieveShowData() {
      // MR-49 (P5-8): hand-rolled XHR Promise -> shared getJson helper. getJson
      // reproduces this method's exact contract (200 -> parsed JSON; non-200 ->
      // Error(`${statusText} (${status})`)), so this is behaviour-preserving.
      return getJson(this.resourceUrlForShowName);
    },

    handleHashtagSearch(e) {
      this.handleOnPressSearch(e);
    },

    setShowTitle() {
      // this sets the show title for the player in the top half of the widget specifically
      if (!this.apiResourceUrl) return;

      this.retrieveShowData().then((res) => {
        this.showTitle = res.show_title;
      });
    },
  },
  mounted() {
    // Init the time remaining (Vue1 `ready()` -> Vue3 `mounted()`).
    // Through metadataDuration() so a missing duration cannot render NaN:NaN on
    // first paint — raised three times by the fold review, and pre-existing.
    this.timeRemaining = this.metadataDuration();
    // PS-5: seed imageType from the initial podcast now that the <img> exists (so
    // the imageType watcher can re-class it), instead of relying on the render of
    // the podcastImage computed to set it as a side effect.
    this.imageType = this.imageTypeForPodcast();
    this.setShowTitle();
  },
  beforeUnmount() {
    // LP-4/MR-78 (fidelity fold, closes PS-12): mirror the v4 Player sibling —
    // the Vue-1 original had no teardown, leaving the <audio> playing and any
    // live hls.js session attached after unmount. Pause playback, then detach
    // the live engine. detachLiveStream is null-safe + throw-safe.
    this.pauseMedia();
    detachLiveStream(this.$refs.player);
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/_helpers';

$screen-sm: 540px;

.player {
  position: relative;
  //border: 1px solid #dadae0;
  border-radius: 2px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  overflow: hidden;
  &--mini {
    max-width: 320px;
    height: 75px;

    @media (min-width: 320px) {
      max-width: 571px;
      width: 100%;
    }

    .player__columns {
      width: 100%;
      flex-direction: row;
    }
  }

  &--classic {
    flex-direction: column;

    @media (min-width: $screen-sm) {
      padding: 1rem;
    }
    .player__content {
      padding: 0 1rem;
    }
  }

  &__columns {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    margin: 0 -0.5rem;
    @media (min-width: $screen-sm) {
      flex-direction: row;
      justify-content: flex-start;
      align-items: stretch;
    }
  }

  &__column {
    padding: 0 0.5rem;
    flex: 1 1 0;
    width: 100%;
    min-width: 0;
      align-self: center;
    &--shrink {
      flex: 0 0 75px;
      width: auto;
      position: relative;
      height: 75px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    &--grow {
      min-width: 0;
      flex: 1 0 50%;
    }
  }

  &__image-wrapper {
    width: 100%;
    height: 0;
    padding-bottom: 100%;
    position: relative;
    margin-bottom: 1rem;
    &--mini {
      position: absolute;
      top: 4px;
      left: 4px;
      width: 67px;
      height: 65px;
      padding-bottom: 0;
    }
  }

  &__image {
    width: 100%;
    position: absolute;
    left: 0;
    object-fit: cover;
    object-position: 50%;
  }

  // #399 artwork cap — v1 (`podcast.vue`) and v4 (`Player.vue`) both cap the
  // cover at 240px below 480px. v3 was left uncapped on the strength of R8's
  // "already mobile-first" finding, which was reached from HORIZONTAL
  // containment evidence only — no assertion ever read this element's HEIGHT.
  // Measured on the deployed build with `albumart=true`: a full-width SQUARE,
  // 320x320 at a 320 viewport, 360x360 at 360 and 390x390 at 390 — 63% above
  // the cap the same change set imposed on the other two versions, pushing the
  // transport row and episode list below the fold. Same #399 symptom, different
  // version.
  //
  // `max-width` alone cannot cap this: the box is an aspect-ratio trick
  // (`height: 0; padding-bottom: 100%`) and percentage padding resolves against
  // the CONTAINING BLOCK's width, not the element's own capped width. So swap to
  // explicit dimensions inside the query. @media(max-width)-gated ONLY, so the
  // frozen desktop oracle is untouched. Mini keeps its own 67x65 box.
  @media (max-width: 480px) {
    &__image-wrapper:not(.player__image-wrapper--mini) {
      max-width: 240px;
      height: 240px;
      padding-bottom: 0;
      margin-left: auto;
      margin-right: auto;

      .player__image {
        height: 100%;
      }
    }
  }

  .rectangle_image {
    position: absolute;
    top: 25%;
    left: 0;
  }

  .square_image {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%
  }
}

.hash-tag-placeholder {
  height: 64px;
  margin-bottom: 8px;
  width: 100%;
}
</style>

<style>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
</style>
